import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { signInAnonymously } from 'firebase/auth';
import { db, auth, isFirebaseConfigured, logEvent } from './firebase-config';
import { doc, getDoc, setDoc, runTransaction } from 'firebase/firestore';
// Import Cloud Functions
import { incrementScore, updateUserEmail, getAccountStatus } from './services/functions';
import { getFunctions, httpsCallable } from 'firebase/functions';
import confetti from 'canvas-confetti';

import './App.css';

// Eager imports — needed on initial render
import StartScreen from './StartScreen';
import Header from './components/Header';
import ClickButton from './components/ClickButton';
import Footer from './components/Footer';
import DevAdBanner from './components/DevAdBanner';
import InstallPromptBanner from './components/InstallPromptBanner';
import StreakWarningBanner from './components/StreakWarningBanner';
import MusicManager, { initializeGlobalAudio } from './components/MusicManager';
import SilentModeDetector from './components/SilentModeDetector';
import ReloadPrompt from './components/ReloadPrompt';
import DailyProgressTracker from './components/DailyProgressTracker';
import BuyMeCoffee from './components/BuyMeCoffee';

// Lazy imports — loaded on demand
const Leaderboard = lazy(() => import('./Leaderboard'));
const SettingsModal = lazy(() => import('./components/SettingsModal'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const Changelog = lazy(() => import('./components/Changelog'));
const PasswordRecovery = lazy(() => import('./components/PasswordRecovery'));
const WeeklyAchievementNotification = lazy(() => import('./components/WeeklyAchievementNotification'));
const ContactModal = lazy(() => import('./components/ContactModal'));
const FAQ = lazy(() => import('./components/FAQ'));
const DebugPanel = lazy(() => import('./components/DebugPanel'));
const IOSInstallModal = lazy(() => import('./components/IOSInstallModal'));

// Debug: Helper function to get current AoE week key
window.getAoeWeekKeyDebug = function(date = new Date()) {
    // Convert to AoE time (UTC-12)
    const aoeDate = new Date(date.getTime() - 12 * 60 * 60 * 1000);
    const d = new Date(Date.UTC(aoeDate.getUTCFullYear(), aoeDate.getUTCMonth(), aoeDate.getUTCDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    const weekKey = `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
    console.log('Week key calculated:', weekKey, 'for date:', date.toISOString());
    return weekKey;
};
import useNetworkStatus from './hooks/useNetworkStatus';
import useFirestoreListeners from './hooks/useFirestoreListeners';
import { capitalizeFirstLetter } from './utils';
import { AntiCheatSystem, WARNING_MESSAGES, prepareValidationData, ANTI_CHEAT_CONFIG } from './antiCheat';
import { USE_SERVER_SCORE_VALIDATION } from './config/featureFlags';
import { migrateMusicVolume } from './utils/musicVolumeMigration';

// Define document paths
const GLOBAL_COUNTER_PATH = 'global/counter';

// Helper function to get current AoE week key
function getAoeWeekKeyClient(date = new Date()) {
    // Convert to AoE time (UTC-12)
    const aoeDate = new Date(date.getTime() - 12 * 60 * 60 * 1000);
    const d = new Date(Date.UTC(aoeDate.getUTCFullYear(), aoeDate.getUTCMonth(), aoeDate.getUTCDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function App() {
    // Page routing state
    const [currentPage, setCurrentPage] = useState('game');
    
    // App state
    const [displayCount, setDisplayCount] = useState(0); // User's personal click count
    const [globalCount, setGlobalCount] = useState(0);   // Total clicks from everyone
    const [localGlobalIncrement, setLocalGlobalIncrement] = useState(0); // Optimistic global display
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const isOnline = useNetworkStatus();
    const [lastClickTime, setLastClickTime] = useState(0);
    const [pendingDbUpdate, setPendingDbUpdate] = useState(0);
    const [isJiggling, setIsJiggling] = useState(false);
    const [clickCombo, setClickCombo] = useState(0);
    const [maxComboAchieved, setMaxComboAchieved] = useState(0);
    const [tierBurst, setTierBurst] = useState(false);
    const [lastTier, setLastTier] = useState(0);
    const [showMaxComboFlash, setShowMaxComboFlash] = useState(false);
    
    // Daily tracking state
    const [dailyClicks, setDailyClicks] = useState(0);
    const [currentStreak, setCurrentStreak] = useState(0);
    const [longestStreak, setLongestStreak] = useState(0);
    const [streakFreezes, setStreakFreezes] = useState(0);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('soundEnabled');
        return saved !== null ? saved === 'true' : true;
    });
    const [sfxVolume, setSfxVolume] = useState(() => {
        const saved = localStorage.getItem('sfxVolume');
        return saved !== null ? parseFloat(saved) : 1.0;
    }); // SFX volume from 0 to 1
    const [musicVolume, setMusicVolume] = useState(() => {
        const saved = localStorage.getItem('musicVolume');
        return saved !== null ? parseFloat(saved) : 1.0;
    }); // Music volume from 0 to 1 (default 100%)
    const [currentCps, setCurrentCps] = useState(0);
    const [maxCps, setMaxCps] = useState(0);
    const [screenShake, setScreenShake] = useState(false);
    
    // Go to leaderboard button state
    const [showGoToLeaderboard, setShowGoToLeaderboard] = useState(false);
    const leaderboardRef = useRef(null);
    const userPositionRef = useRef(null);
    const userInTop10Ref = useRef(null);  // Ref for user's row when in top 10
    
    // Weekly tracking for optimistic updates
    const [weeklyClicks, setWeeklyClicks] = useState(0);
    const [weeklyMaxCombo, setWeeklyMaxCombo] = useState(0);
    const [weeklyMaxCps, setWeeklyMaxCps] = useState(0);
    const [weeklyIncrement, setWeeklyIncrement] = useState(0); // Pending weekly clicks

    // Session management state
    const [gameStarted, setGameStarted] = useState(false);
    const [playerId, setPlayerId] = useState(null);
    const [isBanned, setIsBanned] = useState(false);
    const [banExpiry, setBanExpiry] = useState(null);
    const [banLevel, setBanLevel] = useState(0); // 0=not banned, 1=1hr, 2=24hr, 3=3days, 4=permanent
    const [strikeCount, setStrikeCount] = useState(0);
    const [lastStrikeTime, setLastStrikeTime] = useState(null); // Track when last strike occurred for decay
    const [isGuest, setIsGuest] = useState(false);
    const [showUsernameModal, setShowUsernameModal] = useState(false);
    const [modalOpenedViaChangeUser, setModalOpenedViaChangeUser] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [isCreatingUsername, setIsCreatingUsername] = useState(false);

    // Anti-cheat state
    const [warningMessage, setWarningMessage] = useState('');
    const [debugInfo, setDebugInfo] = useState(null);
    const [cooldownRemaining, setCooldownRemaining] = useState(0);
    const antiCheatRef = useRef(null);
    const cooldownIntervalRef = useRef(null);
    const cpsHistoryRef = useRef([]);
    const sessionPeakCpsRef = useRef(0); // Ref for immediate access to peak CPS
    
    // Password reset state
    const [resetToken, setResetToken] = useState(null);
    
    // Weekly achievement notification state
    const [weeklyAchievementsToShow, setWeeklyAchievementsToShow] = useState([]);
    const [showWeeklyNotification, setShowWeeklyNotification] = useState(false);
    const [showViewAchievementButton, setShowViewAchievementButton] = useState(false);
    const [storedWeeklyAchievements, setStoredWeeklyAchievements] = useState([]);
    
    // Share toast state (centralized for all components)
    const [shareToast, setShareToast] = useState(null);

    // Settings state
    const [showSettings, setShowSettings] = useState(false);
    const [hasEmail, setHasEmail] = useState(false);
    const [userStats, setUserStats] = useState({ 
        totalClicks: 0, 
        maxCombo: 0, 
        maxCps: 0,
        createdAt: null,
        weeklyTop10s: [],
        weeklyTop10Count: 0,
        weeklyTop10ByCategory: {
            value: 0,
            maxCombo: 0,
            maxCps: 0
        }
    });
    const [overallMaxCps, setOverallMaxCps] = useState(0); // Track overall max CPS from database
    const [overallMaxCombo, setOverallMaxCombo] = useState(0); // Track overall max combo from database
    
    // PWA Install state
    const [installPrompt, setInstallPrompt] = useState(null);
    const [showInstallBanner, setShowInstallBanner] = useState(false);
    const [showIOSInstallModal, setShowIOSInstallModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    // Audio context and master gain refs
    const audioContextRef = useRef(null);
    const masterGainRef = useRef(null);
    
    // Audio failure callback ref for silent mode detection
    const audioFailureCallbackRef = useRef(null);
    
    // Initialize audio context with better settings
    const initializeAudio = useCallback(() => {
        if (!audioContextRef.current) {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContext({
                latencyHint: 'interactive' // Low latency for games
            });
            
            // Create master gain node for volume control
            masterGainRef.current = audioContextRef.current.createGain();
            masterGainRef.current.connect(audioContextRef.current.destination);
            masterGainRef.current.gain.value = sfxVolume;
        }
        
        // Resume audio context on user interaction (browser requirement)
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
    }, [sfxVolume]);

    // Migrate music volume from old default (50%) to new default (100%)
    useEffect(() => {
        migrateMusicVolume();
    }, []); // Run once on mount
    
    // Update master volume when sfxVolume changes and persist
    useEffect(() => {
        if (masterGainRef.current) {
            masterGainRef.current.gain.value = sfxVolume;
        }
        localStorage.setItem('sfxVolume', sfxVolume.toString());
    }, [sfxVolume]);
    
    // Persist sound enabled state
    useEffect(() => {
        localStorage.setItem('soundEnabled', soundEnabled.toString());
    }, [soundEnabled]);
    
    // Persist music volume
    useEffect(() => {
        localStorage.setItem('musicVolume', musicVolume.toString());
    }, [musicVolume]);
    
    // Initialize anti-cheat system
    useEffect(() => {
        if (gameStarted) {
            antiCheatRef.current = new AntiCheatSystem();
        }
        return () => {
            if (cooldownIntervalRef.current) {
                clearInterval(cooldownIntervalRef.current);
            }
        };
    }, [gameStarted]);

    // --- Sound generation functions ---
    const playClickSound = useCallback(() => {
        if (!soundEnabled) return;
        
        // Initialize audio on first interaction
        initializeAudio();
        
        if (!audioContextRef.current || !masterGainRef.current) return;
        
        try {
            const audioContext = audioContextRef.current;
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            const panNode = audioContext.createStereoPanner ? audioContext.createStereoPanner() : null;
            
            // Pitch based on CPS - starts at 600Hz, increases with speed
            // CPS 0-10 maps to 600-1200Hz
            const cpsMultiplier = Math.min(currentCps / 10, 1); // Cap at CPS 10
            const frequency = 600 + (600 * cpsMultiplier);
            
            // Connect nodes: oscillator -> gain -> pan -> master
            oscillator.connect(gainNode);
            if (panNode) {
                gainNode.connect(panNode);
                // Add subtle stereo panning (±0.03-0.05, bias center on mobile)
                const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
                const panValue = isMobile ? 0 : (Math.random() * 0.06 - 0.03);
                panNode.pan.value = panValue;
                panNode.connect(masterGainRef.current);
            } else {
                gainNode.connect(masterGainRef.current);
            }
            
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
            
            // Click sound envelope (slightly louder)
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
            
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.1);
        } catch (err) {
            console.error("Error playing click sound:", err);
        }
    }, [soundEnabled, initializeAudio, currentCps]);

    const playComboSound = useCallback(() => {
        if (!soundEnabled) return;
        
        // Initialize audio on first interaction
        initializeAudio();
        
        if (!audioContextRef.current || !masterGainRef.current) return;
        
        try {
            const audioContext = audioContextRef.current;
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(masterGainRef.current);
            
            oscillator.type = 'sine';
            
            // Ascending tone based on combo tier
            // Higher combos = higher starting pitch
            const comboMultiplier = Math.min(clickCombo / 100, 1); // Cap at combo 100
            const startFreq = 400 + (200 * comboMultiplier); // 400-600Hz start
            const endFreq = 800 + (400 * comboMultiplier);   // 800-1200Hz end
            
            oscillator.frequency.setValueAtTime(startFreq, audioContext.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(endFreq, audioContext.currentTime + 0.2);
            
            gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
            
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.2);
        } catch (err) {
            console.error("Error playing combo sound:", err);
        }
    }, [soundEnabled, initializeAudio, clickCombo]);

    const playAchievementSound = useCallback(() => {
        if (!soundEnabled) return;
        
        // Initialize audio on first interaction
        initializeAudio();
        
        if (!audioContextRef.current || !masterGainRef.current) return;
        
        try {
            const audioContext = audioContextRef.current;
            
            // Create multiple oscillators for a rich bell-like "ding!"
            const fundamental = audioContext.createOscillator();
            const harmonic1 = audioContext.createOscillator();
            const harmonic2 = audioContext.createOscillator();
            const harmonic3 = audioContext.createOscillator();
            
            const fundamentalGain = audioContext.createGain();
            const harmonic1Gain = audioContext.createGain();
            const harmonic2Gain = audioContext.createGain();
            const harmonic3Gain = audioContext.createGain();
            
            // Mix all harmonics
            const mixGain = audioContext.createGain();
            
            // Connect oscillators to gains
            fundamental.connect(fundamentalGain);
            harmonic1.connect(harmonic1Gain);
            harmonic2.connect(harmonic2Gain);
            harmonic3.connect(harmonic3Gain);
            
            // Connect to mixer
            fundamentalGain.connect(mixGain);
            harmonic1Gain.connect(mixGain);
            harmonic2Gain.connect(mixGain);
            harmonic3Gain.connect(mixGain);
            
            // Connect to master
            mixGain.connect(masterGainRef.current);
            
            // Bell-like frequencies (fundamental at 2093 Hz = C7)
            const baseFreq = 2093; // C7 for a very bright, clear "ding!"
            
            fundamental.type = 'sine';
            fundamental.frequency.value = baseFreq;
            
            // Add harmonics for metallic bell sound
            harmonic1.type = 'sine';
            harmonic1.frequency.value = baseFreq * 2; // Octave for clarity
            
            harmonic2.type = 'sine';
            harmonic2.frequency.value = baseFreq * 3.2; // Bright harmonic
            
            harmonic3.type = 'sine';
            harmonic3.frequency.value = baseFreq * 4.5; // Extra sparkle
            
            // Quick attack, natural decay for bell sound
            const now = audioContext.currentTime;
            
            // Fundamental envelope - stronger attack for "ding!"
            fundamentalGain.gain.setValueAtTime(0, now);
            fundamentalGain.gain.linearRampToValueAtTime(0.6, now + 0.002); // Very quick, strong attack
            fundamentalGain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
            
            // Harmonic envelopes (faster decay for higher harmonics)
            harmonic1Gain.gain.setValueAtTime(0, now);
            harmonic1Gain.gain.linearRampToValueAtTime(0.25, now + 0.001);
            harmonic1Gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
            
            harmonic2Gain.gain.setValueAtTime(0, now);
            harmonic2Gain.gain.linearRampToValueAtTime(0.15, now + 0.001);
            harmonic2Gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
            
            harmonic3Gain.gain.setValueAtTime(0, now);
            harmonic3Gain.gain.linearRampToValueAtTime(0.08, now + 0.001);
            harmonic3Gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
            
            // Overall mix volume
            mixGain.gain.value = 0.7;
            
            // Start and stop
            fundamental.start(now);
            harmonic1.start(now);
            harmonic2.start(now);
            harmonic3.start(now);
            
            fundamental.stop(now + 0.5);
            harmonic1.stop(now + 0.3);
            harmonic2.stop(now + 0.2);
            harmonic3.stop(now + 0.15);
        } catch (err) {
            console.error("Error playing achievement sound:", err);
        }
    }, [soundEnabled, initializeAudio]);

    // Play epic 100-combo celebration sound - triumphant fanfare
    const play100ComboSound = useCallback(() => {
        // Initialize audio on first interaction
        initializeAudio();
        
        if (!audioContextRef.current || !masterGainRef.current) return;
        
        if (soundEnabled) {
            const audioContext = audioContextRef.current;
            
            // Create a triumphant fanfare with multiple notes
            const notes = [
                { freq: 523.25, start: 0, duration: 0.15 },     // C5
                { freq: 659.25, start: 0.1, duration: 0.15 },   // E5
                { freq: 783.99, start: 0.2, duration: 0.15 },   // G5
                { freq: 1046.50, start: 0.3, duration: 0.3 },   // C6 (held longer)
                { freq: 783.99, start: 0.35, duration: 0.2 },   // G5 harmony
                { freq: 1318.51, start: 0.4, duration: 0.4 },   // E6 (final flourish)
            ];
            
            notes.forEach(note => {
                try {
                    const oscillator = audioContext.createOscillator();
                    const gainNode = audioContext.createGain();
                    
                    // Use triangle wave for a softer, more musical tone
                    oscillator.type = 'triangle';
                    
                    // Connect the nodes
                    oscillator.connect(gainNode);
                    gainNode.connect(masterGainRef.current);
                    
                    // Set frequency
                    oscillator.frequency.setValueAtTime(note.freq, audioContext.currentTime + note.start);
                    
                    // Create envelope for each note
                    const noteTime = audioContext.currentTime + note.start;
                    gainNode.gain.setValueAtTime(0, noteTime);
                    gainNode.gain.linearRampToValueAtTime(0.3, noteTime + 0.02); // Quick attack
                    gainNode.gain.exponentialRampToValueAtTime(0.15, noteTime + note.duration * 0.3); // Decay
                    gainNode.gain.exponentialRampToValueAtTime(0.01, noteTime + note.duration); // Release
                    
                    // Start and stop the oscillator
                    oscillator.start(noteTime);
                    oscillator.stop(noteTime + note.duration);
                    
                    // Add a second oscillator slightly detuned for richness
                    const harmonic = audioContext.createOscillator();
                    const harmonicGain = audioContext.createGain();
                    
                    harmonic.type = 'sine';
                    harmonic.frequency.setValueAtTime(note.freq * 2, audioContext.currentTime + note.start); // Octave up
                    
                    harmonic.connect(harmonicGain);
                    harmonicGain.connect(masterGainRef.current);
                    
                    harmonicGain.gain.setValueAtTime(0, noteTime);
                    harmonicGain.gain.linearRampToValueAtTime(0.1, noteTime + 0.02);
                    harmonicGain.gain.exponentialRampToValueAtTime(0.01, noteTime + note.duration);
                    
                    harmonic.start(noteTime);
                    harmonic.stop(noteTime + note.duration);
                } catch (err) {
                    console.error("Error playing 100-combo sound:", err);
                }
            });
        }
    }, [soundEnabled, initializeAudio]);

    // Refs for listeners
    const lastClickTimeRef = useRef(lastClickTime);
    lastClickTimeRef.current = lastClickTime;
    const displayCountRef = useRef(displayCount);
    displayCountRef.current = displayCount;

    // --- Network Status Listener --- handled by useNetworkStatus hook
    
    useEffect(() => {
        window.scrollTo(0, 0);
        
        // Check for direct page URLs
        const pathname = window.location.pathname;
        if (pathname === '/privacy' || pathname === '/privacy-policy') {
            setCurrentPage('privacy');
            // Update URL to show privacy policy
            window.history.replaceState({}, 'Privacy Policy - Click Mania', '/privacy');
        } else if (pathname === '/terms') {
            setCurrentPage('terms');
            window.history.replaceState({}, 'Terms of Service - Click Mania', '/terms');
        } else if (pathname === '/about') {
            setCurrentPage('about');
            window.history.replaceState({}, 'About - Click Mania', '/about');
        }
        
        // Check for password reset token in URL
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        if (token) {
            setResetToken(token);
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    // Scroll to top when navigating to a page
    useEffect(() => {
        if (currentPage !== 'game') {
            window.scrollTo(0, 0);
        }
    }, [currentPage]);

    // --- Game Start & Reset Handlers ---
    const handleGameStart = (name, isGuestUser = false) => {
        // Check if offline in installed PWA
        if (!isOnline && window.matchMedia('(display-mode: standalone)').matches) {
            setError('Internet connection required to play Click Mania. Please check your connection and try again.');
            return;
        }
        
        const trimmedName = name.trim();

        const startPlayerSession = (id, isGuestSession) => {
            setPlayerId(id);
            setIsGuest(isGuestSession);
            setGameStarted(true);
            setWarningMessage('');
            // Clear sound tip dismissal when starting a new session
            sessionStorage.removeItem('soundTipDismissed');
            // Reset stats for guest sessions
            if (isGuestSession) {
                setDisplayCount(0);
                setMaxComboAchieved(0);
                setMaxCps(0);
                sessionPeakCpsRef.current = 0; // Reset session peak CPS
                setClickCombo(0);
                // Reset weekly stats
                setWeeklyClicks(0);
                setWeeklyMaxCombo(0);
                setWeeklyMaxCps(0);
                setWeeklyIncrement(0);
                // Clear any previous guest session data
                sessionStorage.removeItem('guestScore');
                sessionStorage.removeItem('guestMaxCombo');
                sessionStorage.removeItem('guestMaxCps');
            }
        };

        // If already authenticated from StartScreen, just start the session
        if (auth.currentUser) {
            if (isGuestUser || trimmedName.toLowerCase() === 'guest' || !trimmedName) {
                startPlayerSession(auth.currentUser.uid, true);
            } else {
                startPlayerSession(trimmedName.toLowerCase(), false);
            }
        } else {
            // Fallback for anonymous sign-in if needed
            signInAnonymously(auth)
                .then((credentials) => {
                    if (isGuestUser || trimmedName.toLowerCase() === 'guest' || !trimmedName) {
                        startPlayerSession(credentials.user.uid, true);
                    } else {
                        startPlayerSession(trimmedName.toLowerCase(), false);
                    }
                })
                .catch((err) => {
                    console.error("Anonymous sign-in failed:", err);
                    setError("Could not connect to the game server. Please try again.");
                });
        }
    };

    // Detect crawlers for special handling
    const [isCrawler, setIsCrawler] = useState(false);
    
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const shouldAutostart = params.get('ads_preview') === '1' || params.get('autostart') === '1' || params.get('start') === '1';
        
        // Detect common web crawlers (Google AdSense, Googlebot, etc.)
        const userAgent = navigator.userAgent.toLowerCase();
        const crawlerDetected = /googlebot|adsbot|mediapartners-google|google-adsbots|google-inspectiontool|bingbot|slurp|facebookexternalhit|twitterbot|whatsapp|linkedin/i.test(userAgent);
        
        if (crawlerDetected) {
            console.log('Crawler detected. User agent:', userAgent);
            setIsCrawler(true);
        }
        
        // Auto-start for explicit autostart param only (not for crawlers anymore)
        if (shouldAutostart && !gameStarted) {
            handleGameStart('guest', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGuestSaveScore = useCallback(() => {
        // If guest, show username creation modal
        if (isGuest) {
            setShowUsernameModal(true);
            setModalOpenedViaChangeUser(false);
        }
    }, [isGuest]);

    const handleGoBack = async () => {
        // If guest, show username creation modal instead
        if (isGuest) {
            setShowUsernameModal(true);
            setModalOpenedViaChangeUser(true);
            return;
        }
        
        // Don't clear device authentication when changing user
        // This keeps the username prepopulated
        
        setGameStarted(false);
        setPlayerId(null);
        setIsGuest(false);
        setDisplayCount(0);
        setPendingDbUpdate(0);
        setError(null);
        setWarningMessage('');
        setCooldownRemaining(0);
        // Reset all stats when going back
        setMaxComboAchieved(0);
        setMaxCps(0);
        sessionPeakCpsRef.current = 0; // Reset session peak CPS
        setClickCombo(0);
        setCurrentCps(0);
        // Reset daily tracking state to prevent showing previous user's data
        setDailyClicks(0);
        setCurrentStreak(0);
        setLongestStreak(0);
        setStreakFreezes(0);
        // Clear weekly achievement data to prevent showing previous user's data
        setStoredWeeklyAchievements([]);
        setShowViewAchievementButton(false);
        setWeeklyAchievementsToShow([]);
        // Clear sound tip dismissal when changing users
        sessionStorage.removeItem('soundTipDismissed');
        if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
        }
        if (antiCheatRef.current) {
            antiCheatRef.current.reset();
        }
    };

    const getUserDocPath = useCallback(() => {
        if (!playerId) return null;
        const collection = isGuest ? 'guests' : 'users';
        return doc(db, collection, playerId);
    }, [playerId, isGuest]);

    // --- Combo Reset Effect ---
    useEffect(() => {
        if (clickCombo === 0) return;
        const timer = setTimeout(() => {
            setClickCombo(0);
            setCurrentCps(0); // Clear CPS when combo ends
        }, 500); // Reset after 500ms of inactivity
        return () => clearTimeout(timer);
    }, [lastClickTime, clickCombo]);

    // --- CPS Calculation Effect ---
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            const fiveSecondsAgo = now - 5000;
            
            // Remove clicks older than 5 seconds
            const recentClicks = cpsHistoryRef.current.filter(timestamp => timestamp > fiveSecondsAgo);
            cpsHistoryRef.current = recentClicks;
            
            // Calculate current CPS (clicks in last 5 seconds / 5)
            const cps = recentClicks.length / 5;
            setCurrentCps(cps);
            
            // Update session peak CPS if current is higher (absolute highest in this session)
            if (cps > sessionPeakCpsRef.current) {
                sessionPeakCpsRef.current = cps;
            }
            
            // Update max CPS display (for UI)
            setMaxCps(prevMaxCps => Math.max(prevMaxCps, cps));
        }, 100); // Update every 100ms for smooth display
        
        return () => clearInterval(interval);
    }, []);


    // --- Tier Burst Effect ---
    useEffect(() => {
        let shouldTriggerBurst = false;
        
        // Trigger burst every 10 clicks (smoother progression)
        if (clickCombo > 0 && clickCombo % 10 === 0 && clickCombo !== lastTier) {
            shouldTriggerBurst = true;
        }
        
        if (shouldTriggerBurst) {
            setTierBurst(true);
            setLastTier(clickCombo);
            // Trigger screen shake on tier burst
            setScreenShake(true);
            setTimeout(() => setScreenShake(false), 150); // 150ms shake duration
            // Play combo sound only if sound is enabled
            if (soundEnabled) {
                playComboSound();
            }
            
            // Special celebration for hitting every 100 combo milestone!
            if (clickCombo >= 100 && clickCombo % 100 === 0) {
                // Check for reduced motion preference
                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                
                if (!prefersReducedMotion) {
                    // Detect if mobile for performance optimization
                    const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
                    const maxParticles = isMobile ? 80 : 150;
                    
                    // Fire multiple confetti bursts for big celebration
                    const duration = 3 * 1000;
                    const animationEnd = Date.now() + duration;
                    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };
                    
                    function randomInRange(min, max) {
                        return Math.random() * (max - min) + min;
                    }
                    
                    const interval = setInterval(function() {
                        const timeLeft = animationEnd - Date.now();
                        
                        if (timeLeft <= 0) {
                            return clearInterval(interval);
                        }
                        
                        const particleCount = maxParticles * (timeLeft / duration);
                        
                        // Confetti from both sides
                        confetti({
                            ...defaults,
                            particleCount: Math.floor(particleCount / 2),
                            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
                        });
                        confetti({
                            ...defaults,
                            particleCount: Math.floor(particleCount / 2),
                            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
                        });
                    }, 250);
                }
                
                // Extra strong screen shake for 100
                setScreenShake(true);
                setTimeout(() => setScreenShake(false), 300); // Longer shake
                
                // Play epic celebration sound for 100-combo milestones
                if (soundEnabled) {
                    play100ComboSound();
                }
                
                // Show gold flash overlay
                setShowMaxComboFlash(true);
                setTimeout(() => setShowMaxComboFlash(false), 800);
            }
        }
    }, [clickCombo, lastTier, soundEnabled, playComboSound, playAchievementSound]);

    // Create a ref to store the debounce timer
    const updateTimerRef = useRef(null);
    
    // Create refs for values used in the effect to avoid re-runs
    const clickComboRef = useRef(clickCombo);
    const soundEnabledRef = useRef(soundEnabled);
    const sfxVolumeRef = useRef(sfxVolume);
    const maxCpsRef = useRef(maxCps);
    
    // Update refs when values change
    useEffect(() => {
        clickComboRef.current = clickCombo;
        soundEnabledRef.current = soundEnabled;
        sfxVolumeRef.current = sfxVolume;
        maxCpsRef.current = maxCps;
    }, [clickCombo, soundEnabled, sfxVolume, maxCps]);
    
    // --- Database Update Effect ---
    useEffect(() => {
        if (!gameStarted || !isOnline || pendingDbUpdate === 0) return;

        // Clear any existing timer
        if (updateTimerRef.current) {
            clearTimeout(updateTimerRef.current);
        }

        // Set new timer
        updateTimerRef.current = setTimeout(() => {
            const globalDoc = doc(db, GLOBAL_COUNTER_PATH);
            
            // Capture the current value before resetting
            const clicksToAdd = pendingDbUpdate;
            
            // Early exit if no clicks to add
            if (clicksToAdd === 0) {
                console.log('No clicks to add, skipping update');
                return;
            }
            
            // Reset pendingDbUpdate to 0 to prevent double-sending
            setPendingDbUpdate(0);

            // For guests, increment with click cap enforcement
            if (isGuest) {
                incrementScore(clicksToAdd, null, true, {})
                    .then((result) => {
                        if (result?.globalValue != null) {
                            setGlobalCount(result.globalValue);
                            setLocalGlobalIncrement(0);
                        }
                    })
                    .catch(err => {
                        if (err?.code === 'resource-exhausted') {
                            // Guest click limit reached — force account creation
                            setShowUsernameModal(true);
                        } else {
                            console.error("Guest score update failed:", err);
                            setPendingDbUpdate(prev => prev + clicksToAdd);
                        }
                    });
                return;
            }

            // For named users, update both user and global counters
            const userDoc = getUserDocPath();
            if (!userDoc) return;

            // Prepare anti-cheat data for server
            const antiCheatData = antiCheatRef.current ? 
                prepareValidationData(antiCheatRef.current) : null;

            if (USE_SERVER_SCORE_VALIDATION) {
                const sessionData = {
                    ...antiCheatData,
                    currentCombo: clickCombo,  // Current combo for this session
                    currentCps: currentCps,     // Current CPS for this session
                    maxCombo: maxComboAchieved, // Session max combo
                    maxCps: sessionPeakCpsRef.current,   // Session peak CPS (absolute highest)
                    soundEnabled: soundEnabledRef.current,
                    volume: sfxVolumeRef.current
                };
                
                console.log('📤 Calling incrementScore with:', {
                    clicksToAdd,
                    playerId,
                    isGuest: false,
                    sessionData
                });

                incrementScore(clicksToAdd, playerId, false, sessionData)
                    .then((result) => {
                        console.log('✅ incrementScore success:', result);
                        // Use Cloud Function response instead of re-reading (#2/#12)
                        if (result?.globalValue != null) {
                            setGlobalCount(result.globalValue);
                        }
                        setLocalGlobalIncrement(0);

                        if (result?.weeklyValue != null) {
                            setWeeklyClicks(result.weeklyValue);
                            setWeeklyIncrement(0);
                        }

                        if (result?.dailyClicks != null) {
                            setDailyClicks(result.dailyClicks);
                        }
                        if (result?.currentStreak != null) {
                            setCurrentStreak(result.currentStreak);
                        }
                        if (result?.longestStreak != null) {
                            setLongestStreak(result.longestStreak);
                        }
                        if (result?.streakFreezes != null) {
                            setStreakFreezes(result.streakFreezes);
                        }
                    })
                    .catch(err => {
                        console.error("❌ Score update failed:", err);
                        console.error("Error details:", err.code, err.message);
                        // Add clicks back on error so they can be retried
                        setPendingDbUpdate(prev => prev + clicksToAdd);
                    });
            } else {
                // Fallback to direct transaction
                runTransaction(db, async (transaction) => {
                const [userDocSnap, globalDocSnap] = await Promise.all([
                    transaction.get(userDoc),
                    transaction.get(globalDoc)
                ]);

                const currentUserVal = userDocSnap.data()?.value || 0;
                const newTotalUserVal = currentUserVal + clicksToAdd;
                
                const currentGlobalVal = globalDocSnap.data()?.value || 0;
                const newTotalGlobalVal = currentGlobalVal + clicksToAdd;

                // Update max combo if current combo is higher
                const currentMaxCombo = userDocSnap.data()?.maxCombo || 0;
                const updatedMaxCombo = Math.max(currentMaxCombo, maxComboAchieved);
                
                // Update max CPS if session peak is higher
                const currentMaxCps = userDocSnap.data()?.maxCps || 0;
                const updatedMaxCps = Math.max(currentMaxCps, sessionPeakCpsRef.current);

                transaction.set(userDoc, { 
                    value: newTotalUserVal,
                    lastUpdate: Date.now(),
                    antiCheatData: antiCheatData,
                    soundEnabled: soundEnabledRef.current,
                    volume: sfxVolumeRef.current,
                    maxCombo: updatedMaxCombo,
                    maxCps: updatedMaxCps
                }, { merge: true });
                transaction.set(globalDoc, { value: newTotalGlobalVal });
            }).then(() => {
                // Force a poll to get the updated value, then reset local increment
                getDoc(globalDoc).then(snap => {
                    if (snap.exists()) {
                        setGlobalCount(snap.data().value || 0);
                        // Only reset local increment after we've updated the global count
                        setLocalGlobalIncrement(0);
                    }
                });
            }).catch((err) => {
                console.error("Transaction failed: ", err);
                setPendingDbUpdate(prev => prev + clicksToAdd);
            });
            }
        }, 500); // Match combo timeout of 500ms to capture complete combos

        // Cleanup function
        return () => {
            if (updateTimerRef.current) {
                clearTimeout(updateTimerRef.current);
            }
        };
    }, [pendingDbUpdate, gameStarted, isOnline, isGuest, playerId]); // Only essential dependencies!

    // --- Firestore Listener Effects --- handled by custom hook
    // Callback to process weekly achievement data loaded during init (#4 consolidated)
    const handleWeeklyAchievementData = useCallback((data) => {
        const { weeklyTop10s, pendingAchievements, weeklyParticipation, pendingWeeklyParticipation } = data;

        let allWeeklyData = [...weeklyTop10s];
        let allPendingData = [...pendingAchievements];

        weeklyParticipation.forEach(p => allWeeklyData.push(p));
        pendingWeeklyParticipation.forEach(p => allPendingData.push(p));

        if (allWeeklyData.length > 0) {
            const latestWeek = allWeeklyData.reduce((latest, item) => item.week > latest ? item.week : latest, '');
            const latestWeekData = allWeeklyData.filter(a => a.week === latestWeek);
            if (latestWeekData.length > 0) {
                setStoredWeeklyAchievements(latestWeekData);
                setShowViewAchievementButton(true);
            } else {
                setStoredWeeklyAchievements([]);
                setShowViewAchievementButton(false);
            }
        } else {
            setStoredWeeklyAchievements([]);
            setShowViewAchievementButton(false);
        }

        if (allPendingData.length > 0) {
            setWeeklyAchievementsToShow(allPendingData);
            setShowWeeklyNotification(true);
            setStoredWeeklyAchievements(allPendingData);
        }
    }, []);

    useFirestoreListeners({
        gameStarted,
        isOnline,
        playerId,
        isGuest,
        setDisplayCount,
        setGlobalCount,
        setLocalGlobalIncrement,
        setIsLoading,
        setError,
        setOverallMaxCps,
        setOverallMaxCombo,
        setDailyClicks,
        setCurrentStreak,
        setLongestStreak,
        setStreakFreezes,
        setSoundEnabled,
        setWeeklyAchievementData: handleWeeklyAchievementData,
    });

    // --- Load Weekly Data Effect ---
    useEffect(() => {
        if (!gameStarted || !playerId || isGuest) return;

        const weekKey = getAoeWeekKeyClient();
        const weeklyUserDoc = doc(db, 'weekly', weekKey, 'users', playerId.toLowerCase());
        
        // Load weekly data
        getDoc(weeklyUserDoc).then((doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setWeeklyClicks(data.value || 0);
                setWeeklyMaxCombo(data.maxCombo || 0);
                setWeeklyMaxCps(data.maxCps || 0);
            } else {
                // No weekly data yet, start at 0
                setWeeklyClicks(0);
                setWeeklyMaxCombo(0);
                setWeeklyMaxCps(0);
            }
        }).catch(err => {
            console.error("Failed to load weekly data:", err);
        });
    }, [gameStarted, playerId, isGuest]);

    // Sound preference and weekly achievements now loaded in useFirestoreListeners init (#4/#9)

    // Load user data when settings modal opens.
    // Email status is fetched server-side via getAccountStatus, which only returns
    // a hasEmail boolean - the actual address is never sent to the client.
    useEffect(() => {
        if (showSettings && !isGuest && playerId) {
            getAccountStatus()
                .then(({ hasEmail: serverHasEmail }) => setHasEmail(Boolean(serverHasEmail)))
                .catch((err) => console.error('Failed to load account status:', err));

            const userDoc = getUserDocPath();
            if (userDoc) {
                getDoc(userDoc).then((doc) => {
                    if (doc.exists()) {
                        const data = doc.data();
                        const weeklyTop10s = data.weeklyTop10s || [];
                        
                        // Calculate counts by category
                        const categoryCounts = {
                            value: 0,
                            maxCombo: 0,
                            maxCps: 0
                        };
                        
                        weeklyTop10s.forEach(achievement => {
                            // Handle both 'value' and 'totalClicks' category names
                            if (achievement.category === 'value' || achievement.category === 'totalClicks') {
                                categoryCounts.value++;
                            } else if (achievement.category === 'maxCombo') {
                                categoryCounts.maxCombo++;
                            } else if (achievement.category === 'maxCps') {
                                categoryCounts.maxCps++;
                            }
                        });
                        
                        setUserStats({
                            totalClicks: data.value || 0,
                            maxCombo: data.maxCombo || 0,
                            maxCps: data.maxCps || 0,
                            createdAt: data.createdAt || null,
                            weeklyTop10s: weeklyTop10s,
                            weeklyTop10Count: data.weeklyTop10Count || 0,
                            weeklyTop10ByCategory: categoryCounts
                        });
                    }
                }).catch((err) => {
                    console.error("Failed to load user data:", err);
                });
            }
        }
    }, [showSettings, playerId, isGuest, getUserDocPath]);
    
    // PWA Install Detection
    useEffect(() => {
        // Check if iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);
        
        // Check if running as standalone app
        const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                                   window.navigator.standalone || 
                                   document.referrer.includes('android-app://');
        setIsStandalone(isInStandaloneMode);
        
        // Handle Android/Desktop install prompt
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
            
            // Check if user has dismissed the banner before
            const dismissedTime = localStorage.getItem('installBannerDismissed');
            if (!dismissedTime) {
                setShowInstallBanner(true);
            } else {
                // Show again after 7 days
                const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
                if (daysSinceDismissed > 7) {
                    setShowInstallBanner(true);
                }
            }
        };
        
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        
        // Hide banner if app gets installed
        const handleAppInstalled = () => {
            setShowInstallBanner(false);
            setInstallPrompt(null);
        };
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);
    
    const handleInstallClick = async () => {
        if (!installPrompt) return;
        
        setShowInstallBanner(false);
        installPrompt.prompt();
        
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
            setInstallPrompt(null);
        }
    };
    
    const handleInstallDismiss = () => {
        setShowInstallBanner(false);
        localStorage.setItem('installBannerDismissed', Date.now().toString());
    };

    const toggleSound = async () => {
        const newSoundState = !soundEnabled;
        setSoundEnabled(newSoundState);

        if (!isGuest && playerId) {
            const userDoc = getUserDocPath();
            if (userDoc) {
                try {
                    await setDoc(userDoc, { soundEnabled: newSoundState }, { merge: true });
                } catch (err) {
                    console.error("Failed to save sound preference:", err);
                }
            }
        }
    };

    // const handleUsernameChanged = (newUsername) => {
    //     setPlayerId(newUsername);
    //     localStorage.setItem('lastPlayedUsername',
    //         newUsername.charAt(0).toUpperCase() + newUsername.slice(1));
    //     setSuccessMessage('Username changed successfully!');
    //     setTimeout(() => setSuccessMessage(null), 3000);
    //     setShowSettings(false);
    // };

    const handleShare = async (text, source = 'unknown') => {
        logEvent('share', { source, method: navigator.share ? 'native' : 'clipboard' });
        if (navigator.share) {
            try {
                await navigator.share({ title: 'Click Mania', text });
            } catch (err) {
                if (err.name !== 'AbortError') console.error('Share failed:', err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(text);
                setShareToast(text);
                setTimeout(() => setShareToast(null), 4000);
            } catch (err) {
                console.error('Clipboard write failed:', err);
            }
        }
    };

    // Called by SettingsModal. Server enforces the old-email match; we just pass through.
    const handleEmailUpdate = async (oldEmail, newEmail) => {
        if (!playerId || isGuest) {
            throw new Error('Email can only be set for registered users');
        }
        await updateUserEmail(playerId, oldEmail, newEmail);
        // Refetch status so the indicator reflects the change without revealing the address
        const status = await getAccountStatus();
        setHasEmail(Boolean(status.hasEmail));
    };

    // --- Username Creation Handler ---
    const handleUsernameCreation = async (e) => {
        e.preventDefault();
        setUsernameError('');
        setIsCreatingUsername(true);

        const trimmedUsername = newUsername.trim().toLowerCase();
        
        // Validate username
        if (!trimmedUsername || trimmedUsername === 'guest') {
            setUsernameError('Please enter a valid username');
            setIsCreatingUsername(false);
            return;
        }

        try {
            // Check if username already exists
            const userDoc = doc(db, 'users', trimmedUsername);
            const userSnapshot = await getDoc(userDoc);
            
            if (userSnapshot.exists()) {
                setUsernameError('Username already taken');
                setIsCreatingUsername(false);
                return;
            }

            // Store guest score temporarily
            sessionStorage.setItem('guestScore', displayCount.toString());
            sessionStorage.setItem('guestMaxCombo', maxComboAchieved.toString());
            sessionStorage.setItem('guestMaxCps', sessionPeakCpsRef.current.toFixed(1));
            sessionStorage.setItem('pendingUsername', trimmedUsername);

            // Redirect to StartScreen for password creation
            setGameStarted(false);
            setShowUsernameModal(false);
            setModalOpenedViaChangeUser(false);
            
        } catch (err) {
            console.error('Error checking username:', err);
            setUsernameError('Failed to check username. Please try again.');
        } finally {
            setIsCreatingUsername(false);
        }
    };

    // --- Increment Counter Function with Anti-Cheat ---
    const handleIncrement = (event) => {
        if (isLoading || !isOnline) return;
        
        // Block clicks if user is banned
        if (isBanned) {
            // Check if temporary ban has expired
            if (banExpiry && Date.now() > banExpiry) {
                setIsBanned(false);
                setBanExpiry(null);
                setStrikeCount(0); // Reset strikes after ban expires
            } else {
                // Show appropriate ban message
                const banMessages = {
                    1: WARNING_MESSAGES.banned_1hr,
                    2: WARNING_MESSAGES.banned_24hr,
                    3: WARNING_MESSAGES.banned_3days,
                    4: WARNING_MESSAGES.banned_permanent
                };
                setWarningMessage(banMessages[banLevel] || WARNING_MESSAGES.banned_permanent);
                return;
            }
        }
        
        // Initialize audio on first user interaction (for returning users who bypass StartScreen)
        initializeGlobalAudio().catch(err => {
            console.log('🎵 Audio initialization from click:', err);
        });

        // Validate click through anti-cheat system
        if (antiCheatRef.current) {
            const validation = antiCheatRef.current.validateClick(event);
            
            // Update debug info
            const stats = antiCheatRef.current.getSessionStats();
            const patternAnalysis = antiCheatRef.current.analyzeClickPattern(Date.now());
            setDebugInfo({
                validation,
                stats,
                patternAnalysis,
                lastClickInterval: antiCheatRef.current.lastClickTime ? Date.now() - antiCheatRef.current.lastClickTime : 0,
                clickHistory: antiCheatRef.current.clickHistory,
                suspiciousScore: antiCheatRef.current.suspiciousScore,
                violationCount: antiCheatRef.current.violationCount
            });
            
            if (!validation.valid) {
                // Handle invalid click
                if (validation.reason === 'cooldown') {
                    return;
                }
                
                // Check for strike decay (1 strike removed per 24 hours of good behavior)
                if (lastStrikeTime && strikeCount > 0) {
                    const hoursSinceLastStrike = (Date.now() - lastStrikeTime) / (1000 * 60 * 60);
                    const strikesToDecay = Math.floor(hoursSinceLastStrike / 24);
                    if (strikesToDecay > 0) {
                        setStrikeCount(prev => Math.max(0, prev - strikesToDecay));
                    }
                }
                
                // Increment strike count
                setStrikeCount(prev => {
                    const newStrikes = prev + 1;
                    setLastStrikeTime(Date.now()); // Update last strike time
                    
                    // Apply ban after 3 strikes
                    if (newStrikes >= 3) {
                        const newBanLevel = Math.min(banLevel + 1, 4);
                        setBanLevel(newBanLevel);
                        setIsBanned(true);
                        
                        // Set ban expiry based on level
                        const banDuration = ANTI_CHEAT_CONFIG.BAN_DURATIONS[newBanLevel - 1];
                        if (banDuration) {
                            setBanExpiry(Date.now() + banDuration);
                        } else {
                            setBanExpiry(null); // Permanent ban
                        }
                        
                        // Trigger ban notification to server
                        if (USE_SERVER_SCORE_VALIDATION && playerId) {
                            // This will be handled by the incrementScore function
                            // which already sends strike count
                        }
                        
                        return 0; // Reset strikes after ban
                    }
                    
                    return newStrikes;
                });
                
                // Reset only the current combo as penalty for cheating
                setClickCombo(0);
                
                // Apply cooldown and show warning
                const cooldownInfo = antiCheatRef.current.applyCooldown(
                    validation.penaltyDuration,
                    validation.reason
                );
                
                // Update suspicious score
                const scoreUpdate = antiCheatRef.current.updateSuspiciousScore(
                    validation.suspiciousIncrement
                );
                
                // Show appropriate warning message with strike count
                if (!isBanned && strikeCount < 2) {
                    setWarningMessage(WARNING_MESSAGES[validation.reason] + ` Strike ${strikeCount + 1}/3!`);
                }
                
                // Start cooldown countdown
                setCooldownRemaining(Math.ceil(cooldownInfo.duration / 1000));
                
                // Clear any existing interval
                if (cooldownIntervalRef.current) {
                    clearInterval(cooldownIntervalRef.current);
                }
                
                // Update countdown every second
                cooldownIntervalRef.current = setInterval(() => {
                    setCooldownRemaining(prev => {
                        if (prev <= 1) {
                            clearInterval(cooldownIntervalRef.current);
                            setWarningMessage('');
                            // Reset the anti-cheat cooldown
                            if (antiCheatRef.current) {
                                antiCheatRef.current.cooldownEndTime = 0;
                            }
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
                
                // IMPORTANT: Return early to prevent logging the invalid click
                return;
            }
        }
        const clickTime = Date.now();
        setIsJiggling(true);
        setLastClickTime(clickTime);
        setClickCombo(prev => {
            const newCombo = prev + 1;
            setMaxComboAchieved(currentMax => Math.max(currentMax, newCombo));
            // Hide Go to Leaderboard button when combo reaches 2 (user starts clicking again)
            if (newCombo >= 2 && showGoToLeaderboard) {
                setShowGoToLeaderboard(false);
            }
            return newCombo;
        });
        setDisplayCount(prev => prev + 1);
        setPendingDbUpdate(prev => prev + 1);
        setLocalGlobalIncrement(prev => prev + 1);

        // Optimistic daily clicks update so progress ring fills live (#3)
        setDailyClicks(prev => prev + 1);

        // Update weekly tracking for optimistic updates (for both guests and users)
        setWeeklyIncrement(prev => prev + 1);
        setWeeklyMaxCombo(prev => Math.max(prev, clickCombo + 1));
        setWeeklyMaxCps(prev => Math.max(prev, currentCps));
        
        // Add click to CPS history
        cpsHistoryRef.current.push(clickTime);
        
        // Play click sound only if sound is enabled
        if (soundEnabled) {
            playClickSound();
        }
    };

    const getComboStatus = () => {
        // Smooth progression - something changes every 10 clicks
        let colorClass = '';
        if (clickCombo > 0) colorClass = 'combo-yellow';    // 1+ clicks
        if (clickCombo >= 20) colorClass = 'combo-orange';  // 20+ clicks
        if (clickCombo >= 40) colorClass = 'combo-red';     // 40+ clicks
        if (clickCombo >= 70) colorClass = 'hot-flame-text'; // 70+ clicks

        let fireClass = '';
        if (clickCombo >= 30) fireClass = 'fire-small';     // 30+ clicks
        if (clickCombo >= 50) fireClass = 'fire-medium';    // 50+ clicks
        if (clickCombo >= 80) fireClass = 'fire-large';     // 80+ clicks

        return {
            colorClass: colorClass,
            fireClass: fireClass,
            showTopLightning: clickCombo >= 60,              // 60+ clicks
            showBottomLightning: clickCombo >= 90,           // 90+ clicks
        };
    };



    // Check Firebase configuration first
    if (!isFirebaseConfigured) {
        return (
            <div className="App">
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <h1>Click Mania</h1>
                    <div style={{ 
                        backgroundColor: '#ffebee', 
                        color: '#c62828', 
                        padding: '1rem', 
                        borderRadius: '4px',
                        margin: '1rem 0'
                    }}>
                        <h2>Firebase Configuration Required</h2>
                        <p>Please set up your Firebase configuration in the .env file:</p>
                        <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                            <li>REACT_APP_API_KEY</li>
                            <li>REACT_APP_AUTH_DOMAIN</li>
                            <li>REACT_APP_PROJECT_ID</li>
                            <li>REACT_APP_STORAGE_BUCKET</li>
                            <li>REACT_APP_MESSAGING_SENDER_ID</li>
                            <li>REACT_APP_APP_ID</li>
                        </ul>
                        <p>Check the README.md for setup instructions.</p>
                    </div>
                </div>
            </div>
        );
    }

    // useMemo must be called before any early returns to satisfy rules of hooks
    const comboStatus = useMemo(() => gameStarted ? getComboStatus() : null, [gameStarted, clickCombo]); // eslint-disable-line react-hooks/exhaustive-deps

    // Show password recovery if token is present (full takeover, unmounts game)
    if (resetToken) {
        return <Suspense fallback={null}><PasswordRecovery token={resetToken} onBack={() => setResetToken(null)} /></Suspense>;
    }

    const pageMap = { privacy: PrivacyPolicy, terms: TermsOfService, about: AboutPage, changelog: Changelog };
    const PageComponent = currentPage !== 'game' ? pageMap[currentPage] : null;

    // Game stays mounted (hidden via CSS) when viewing pages — preserves all state
    return (
        <>
            {/* Page overlay — rendered on top, game hidden behind */}
            {PageComponent && (
                <Suspense fallback={null}>
                    <PageComponent onBack={() => {
                        setCurrentPage('game');
                        window.history.replaceState({}, 'Click Mania', '/');
                    }} />
                </Suspense>
            )}

            {/* Game UI — hidden when a page is active, stays mounted */}
            <div style={{ display: PageComponent ? 'none' : undefined }}>
            {/* Music Manager stays mounted throughout entire app lifecycle */}
            <MusicManager
                combo={clickCombo}
                soundEnabled={soundEnabled}
                musicVolume={musicVolume}
                onAudioFailure={audioFailureCallbackRef}
            />
            <SilentModeDetector
                gameStarted={gameStarted}
                onAudioFailure={audioFailureCallbackRef}
            />
            <ReloadPrompt />
            <Suspense fallback={null}>
            <ContactModal
                isOpen={showContactModal}
                onClose={() => setShowContactModal(false)}
            />
            
            {!gameStarted ? (
                <>
                    <div className="flame-background"></div>
                    <StartScreen 
                        onStart={handleGameStart} 
                        isCrawler={isCrawler}
                        onShowIOSInstall={() => setShowIOSInstallModal(true)}
                        isOnline={isOnline}
                    />
                    <IOSInstallModal 
                        isOpen={showIOSInstallModal}
                        onClose={() => setShowIOSInstallModal(false)}
                    />
                    <FAQ />
                    <Footer
                        onPrivacyClick={() => setCurrentPage('privacy')}
                        onTermsClick={() => setCurrentPage('terms')}
                        onAboutClick={() => setCurrentPage('about')}
                        onContactClick={() => setShowContactModal(true)}
                        onChangelogClick={() => setCurrentPage('changelog')}
                    />
                    <DevAdBanner />
                </>
            ) : (
                <>
            <div className="flame-background"></div>
            <div className={`App ${screenShake ? 'shake-animation' : ''}`}>
                <Header 
                    globalCount={globalCount + localGlobalIncrement}
                    soundEnabled={soundEnabled}
                    toggleSound={toggleSound}
                    setShowSettings={setShowSettings}
                    isOnline={isOnline}
                    handleGoBack={handleGoBack}
                />
                
                {/* PWA Install Banner for non-iOS */}
                {showInstallBanner && !isIOS && (
                    <InstallPromptBanner
                        onInstall={handleInstallClick}
                        onDismiss={handleInstallDismiss}
                    />
                )}

                {/* Streak At-Risk Warning - only for named users */}
                {!isGuest && (
                    <StreakWarningBanner
                        currentStreak={currentStreak}
                        dailyClicks={dailyClicks}
                    />
                )}

                {/* Secondary header row - layout changes based on iOS button visibility */}
                {isIOS && !isStandalone ? (
                    // When iOS button is showing - left/right layout
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        marginTop: '-5px',
                        marginBottom: '10px',
                        minHeight: '28px'
                    }}>
                        {/* Left side - iOS Install button */}
                        <div style={{ paddingLeft: '16px' }}>
                            <button 
                                onClick={() => setShowIOSInstallModal(true)}
                                className="ios-install-hint-main"
                                style={{
                                    padding: '4px 10px',
                                    background: 'transparent',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = 'rgba(255, 255, 255, 0.5)';
                                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                                }}
                            >
                                📱 Add to Home Screen
                            </button>
                        </div>
                        
                        {/* Right side - Playing as (clickable to open stats) */}
                        <div
                            style={{
                                paddingRight: '16px',
                                fontSize: '14px',
                                color: 'rgba(255, 255, 255, 0.8)',
                                cursor: 'pointer'
                            }}
                            onClick={() => setShowSettings(true)}
                            title="View your stats"
                        >
                            Playing as: <span className="user-id-label clickable-username">{isGuest ? 'Guest' : capitalizeFirstLetter(playerId)}</span>
                        </div>
                    </div>
                ) : (
                    // When iOS button is NOT showing - centered layout (original style)
                    <div className="user-id-display" onClick={() => setShowSettings(true)} style={{ cursor: 'pointer' }} title="View your stats">
                        <div>Playing as: <span className="user-id-label clickable-username">{isGuest ? 'Guest' : capitalizeFirstLetter(playerId)}</span></div>
                    </div>
                )}
                
                {/* View Previous Achievement Button - centered */}
                {(showViewAchievementButton || process.env.NODE_ENV === 'development') && !isGuest && storedWeeklyAchievements.length > 0 && (
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                        <button
                            className="view-achievement-button-small"
                            onClick={() => {
                                setWeeklyAchievementsToShow(storedWeeklyAchievements);
                                setShowWeeklyNotification(true);
                            }}
                        >
                            🏆 View Last Week's Achievement
                        </button>
                    </div>
                )}
                
                {isGuest && (
                    <div className="guest-info">
                        <button 
                            className="create-username-button"
                            onClick={() => setShowUsernameModal(true)}
                        >
                            Create Username
                        </button>
                        <p className="guest-note">Create a username to save your score!</p>
                    </div>
                )}

                {error && <div className="error-display"><p>{error}</p></div>}
                {successMessage && <div className="success-display"><p>{successMessage}</p></div>}
                {(warningMessage || cooldownRemaining > 0) && (
                    <div className="warning-display">
                        <p>
                            {warningMessage}
                            {cooldownRemaining > 0 && ` (${cooldownRemaining}s cooldown)`}
                        </p>
                    </div>
                )}
                
                <DebugPanel debugInfo={debugInfo} cooldownRemaining={cooldownRemaining} />
                
                {/* Daily Progress Tracker - only for named users */}
                {!isGuest && (
                    <DailyProgressTracker
                        dailyClicks={dailyClicks}
                        isClicking={clickCombo > 1}
                        currentStreak={currentStreak}
                        streakFreezes={streakFreezes}
                        onShare={handleShare}
                    />
                )}

                <main className="main-content">
                    <p className="count-label">Your Clicks:</p>
                    <div 
                        id="countDisplay" 
                        className={`count-display ${isJiggling ? 'jiggle' : ''} ${comboStatus?.fireClass || ''}`}
                        onAnimationEnd={() => setIsJiggling(false)}
                    >
                        <span className={`count-text ${comboStatus?.colorClass || ''}`}>{isLoading ? '...' : displayCount.toLocaleString()}</span>
                        {comboStatus?.showTopLightning && <div className="lightning-top"></div>}
                        {comboStatus?.showBottomLightning && <div className="lightning-bottom"></div>}
                    </div>
                    
                    
                    <ClickButton
                        clickCombo={clickCombo}
                        currentCps={currentCps}
                        isJiggling={isJiggling}
                        tierBurst={tierBurst}
                        setIsJiggling={setIsJiggling}
                        setTierBurst={setTierBurst}
                        handleIncrement={handleIncrement}
                        isLoading={isLoading}
                        isOnline={isOnline}
                        comboStatus={comboStatus}
                        lastClickTime={lastClickTime}
                        playAchievementSound={playAchievementSound}
                        setShowGoToLeaderboard={setShowGoToLeaderboard}
                    />
                </main>
                
                {/* Reserved space and Go to Leaderboard button */}
                <div style={{ 
                    height: '50px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%',
                    maxWidth: '500px',
                    margin: '0 auto',
                    marginTop: '15px'  // Add breathing room from Click Me button
                }}>
                    {showGoToLeaderboard && (
                        <button
                            className="go-to-leaderboard-button"
                            onClick={(e) => {
                                // Remove focus to prevent highlight
                                e.currentTarget.blur();
                                
                                // Priority: scroll to user's specific position
                                // 1. If user is in top 10, scroll to their row
                                if (userInTop10Ref.current) {
                                    userInTop10Ref.current.scrollIntoView({ 
                                        behavior: 'smooth', 
                                        block: 'center'
                                    });
                                }
                                // 2. If user is outside top 10, scroll to their position section
                                else if (userPositionRef.current) {
                                    userPositionRef.current.scrollIntoView({ 
                                        behavior: 'smooth', 
                                        block: 'center'
                                    });
                                }
                                // 3. Fallback: scroll to leaderboard top
                                else if (leaderboardRef.current) {
                                    leaderboardRef.current.scrollIntoView({ 
                                        behavior: 'smooth', 
                                        block: 'start'
                                    });
                                }
                                // Don't hide the button after clicking - it will hide when combo >= 2
                            }}
                        >
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                gap: '2px',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                                pointerEvents: 'none'
                            }}>
                                <span style={{ 
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    MozUserSelect: 'none',
                                    msUserSelect: 'none'
                                }}>🔥 See your position on the leaderboard! 🔥</span>
                                <span style={{ 
                                    fontSize: '14px',
                                    display: 'inline-block',
                                    animation: 'bounce 1.5s infinite',
                                    lineHeight: '0.8',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    MozUserSelect: 'none',
                                    msUserSelect: 'none'
                                }}>⌄</span>
                            </div>
                        </button>
                    )}
                </div>
                
                <Leaderboard 
                    ref={leaderboardRef}
                    userPositionRef={userPositionRef}
                    userInTop10Ref={userInTop10Ref}
                    playerId={playerId}
                    isGuest={isGuest}
                    displayCount={displayCount}
                    maxComboAchieved={Math.max(maxComboAchieved, overallMaxCombo)}
                    maxCps={Math.max(maxCps, overallMaxCps)}
                    weeklyClicks={weeklyClicks + weeklyIncrement}
                    weeklyMaxCombo={weeklyMaxCombo}
                    weeklyMaxCps={weeklyMaxCps}
                    onGuestSaveScore={handleGuestSaveScore}
                    onShare={handleShare}
                />
                
                {/* Buy Me a Coffee section */}
                <BuyMeCoffee />
                
                <Footer
                    onPrivacyClick={() => setCurrentPage('privacy')}
                    onTermsClick={() => setCurrentPage('terms')}
                    onAboutClick={() => setCurrentPage('about')}
                    onContactClick={() => setShowContactModal(true)}
                    onChangelogClick={() => setCurrentPage('changelog')}
                />
                
                <SettingsModal
                    showSettings={showSettings}
                    setShowSettings={setShowSettings}
                    isGuest={isGuest}
                    userStats={userStats}
                    longestStreak={longestStreak}
                    hasEmail={hasEmail}
                    onUpdateEmail={handleEmailUpdate}
                    soundEnabled={soundEnabled}
                    toggleSound={toggleSound}
                    sfxVolume={sfxVolume}
                    setSfxVolume={setSfxVolume}
                    musicVolume={musicVolume}
                    setMusicVolume={setMusicVolume}
                    onPrivacyClick={() => setCurrentPage('privacy')}
                    onTermsClick={() => setCurrentPage('terms')}
                    onAboutClick={() => setCurrentPage('about')}
                    guestStats={isGuest ? {
                        clicks: displayCount,
                        maxCombo: maxComboAchieved,
                        maxCps: maxCps
                    } : null}
                    onCreateUser={handleGuestSaveScore}
                    onShowIOSInstall={() => setShowIOSInstallModal(true)}
                    isIOS={isIOS}
                    isStandalone={isStandalone}
                    playerId={playerId}
                    // onUsernameChanged={handleUsernameChanged}
                    onShare={handleShare}
                />
                
                {/* Username Creation Modal */}
                {showUsernameModal && (
                    <div className="modal-overlay" onClick={() => setShowUsernameModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2>Create Username</h2>
                            <p className="save-stats-title">Save your current stats!</p>
                            <div className="modal-stats">
                                <div className="stat-item">
                                    <span className="stat-label">Clicks:</span>
                                    <span className="stat-value">{displayCount.toLocaleString()}</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-label">Max Combo:</span>
                                    <span className="stat-value">{maxComboAchieved}</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-label">Max CPS:</span>
                                    <span className="stat-value">{maxCps.toFixed(1)}</span>
                                </div>
                            </div>
                            <form onSubmit={handleUsernameCreation}>
                                <input
                                    type="text"
                                    placeholder="Enter username"
                                    value={newUsername}
                                    onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
                                    maxLength={20}
                                    required
                                />
                                {usernameError && <p className="error-message">{usernameError}</p>}
                                <div className="modal-buttons">
                                    <button type="submit" disabled={isCreatingUsername}>
                                        {isCreatingUsername ? 'Creating...' : 'Create'}
                                    </button>
                                    <button type="button" onClick={() => {
                                        setShowUsernameModal(false);
                                        setModalOpenedViaChangeUser(false);
                                    }}>
                                        Cancel
                                    </button>
                                    {isGuest && modalOpenedViaChangeUser && (
                                        <button 
                                            type="button" 
                                            onClick={() => {
                                                setShowUsernameModal(false);
                                                setModalOpenedViaChangeUser(false);
                                                setGameStarted(false);
                                                setPlayerId(null);
                                                setIsGuest(false);
                                                setDisplayCount(0);
                                                setPendingDbUpdate(0);
                                                setError(null);
                                                setWarningMessage('');
                                                setCooldownRemaining(0);
                                                setMaxComboAchieved(0);
                                                setMaxCps(0);
                                                sessionPeakCpsRef.current = 0; // Reset session peak CPS
                                                setClickCombo(0);
                                                setCurrentCps(0);
                                                // Reset weekly stats
                                                setWeeklyClicks(0);
                                                setWeeklyMaxCombo(0);
                                                setWeeklyMaxCps(0);
                                                setWeeklyIncrement(0);
                                                // Clear sound tip dismissal when exiting
                                                sessionStorage.removeItem('soundTipDismissed');
                                                if (cooldownIntervalRef.current) {
                                                    clearInterval(cooldownIntervalRef.current);
                                                }
                                            }}
                                            className="exit-button"
                                        >
                                            Exit and lose my stats
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                
                {/* Dev Testing Button for Weekly Achievement Notification */}
                {process.env.NODE_ENV === 'development' && (
                    <>
                        <button
                            onClick={() => {
                                // Create mock achievements for testing
                                const mockAchievements = [
                                    {
                                        week: '2025-W33',
                                        position: 2,
                                        category: 'totalClicks',
                                        value: 5000,
                                        maxCombo: 45,
                                        maxCps: 18
                                    },
                                    {
                                        week: '2025-W33',
                                        position: 3,
                                        category: 'maxCps',
                                        value: 3200,
                                        maxCombo: 38,
                                        maxCps: 23
                                    }
                                ];
                                setWeeklyAchievementsToShow(mockAchievements);
                                setShowWeeklyNotification(true);
                            }}
                            style={{
                                position: 'fixed',
                                bottom: '20px',
                                left: '20px',
                                padding: '10px 20px',
                                backgroundColor: '#9333ea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        🧪 Test Weekly Achievement
                    </button>
                    <button
                        onClick={async () => {
                            // eslint-disable-next-line no-alert
                            const adminPassword = window.prompt('Admin password (ADMIN_PASSWORD env var):');
                            if (!adminPassword) return;
                            try {
                                const functions = getFunctions();
                                const finalizeLastWeek = httpsCallable(functions, 'finalizeLastWeek');
                                console.log('Finalizing last week...');
                                const result = await finalizeLastWeek({ adminPassword });
                                console.log('Result:', result.data);
                                alert(result.data.message || 'Operation completed');
                            } catch (error) {
                                console.error('Error finalizing last week:', error);
                                alert('Error: ' + error.message);
                            }
                        }}
                        style={{
                            position: 'fixed',
                            bottom: '120px',
                            left: '20px',
                            padding: '10px 20px',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        📊 Finalize Last Week
                    </button>
                    <button
                        onClick={async () => {
                            // eslint-disable-next-line no-alert
                            const adminPassword = window.prompt('Admin password (ADMIN_PASSWORD env var):');
                            if (!adminPassword) return;
                            try {
                                const functions = getFunctions();
                                const migrateAuthFields = httpsCallable(functions, 'migrateAuthFields');
                                console.log('Migrating auth fields from /users to /userAuth...');
                                const result = await migrateAuthFields({ adminPassword });
                                console.log('Migration complete:', result.data);
                                alert(`Migrated ${result.data.migrated} users to /userAuth (${result.data.skipped} skipped — already migrated or no auth fields)`);
                            } catch (error) {
                                console.error('Error migrating auth fields:', error);
                                alert('Error: ' + error.message);
                            }
                        }}
                        style={{
                            position: 'fixed',
                            bottom: '170px',
                            left: '20px',
                            padding: '10px 20px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        🔐 Migrate Auth Fields → /userAuth
                    </button>
                    </>
                )}
                
                {/* Centralized share toast */}
                {shareToast && (
                    <div className="share-toast" onClick={() => setShareToast(null)}>
                        <div className="share-toast-header">Copied to clipboard!</div>
                        <div className="share-toast-text">{shareToast}</div>
                    </div>
                )}

                {/* Gold flash overlay for 100 combo milestones */}
                {showMaxComboFlash && <div className="max-combo-flash"></div>}
                
                {/* iOS Install Modal */}
                <IOSInstallModal 
                    isOpen={showIOSInstallModal}
                    onClose={() => setShowIOSInstallModal(false)}
                />
                
                {/* Weekly Achievement Notification */}
                {showWeeklyNotification && (
                    <WeeklyAchievementNotification
                        weeklyAchievements={weeklyAchievementsToShow}
                        soundEnabled={soundEnabled}
                        sfxVolume={sfxVolume}
                        onDismiss={() => setShowWeeklyNotification(false)}
                        onClearData={async () => {
                            // Clear pending achievements from Firestore immediately when dismissed
                            if (!isGuest && playerId) {
                                try {
                                    const functions = getFunctions();
                                    const clearAchievements = httpsCallable(functions, 'clearPendingAchievements');
                                    // Fire and forget - don't await blocking UI
                                    clearAchievements({ username: playerId })
                                        .then(() => console.log("Cleared pending achievements"))
                                        .catch(err => console.error("Failed to clear pending achievements:", err));
                                } catch (err) {
                                    console.error("Failed to initiate clear achievements:", err);
                                }
                            }
                        }}
                        onOpenSettings={() => {
                            setShowSettings(true);
                        }}
                    />
                )}
            </div>
                </>
            )}
            </Suspense>
            </div>
        </>
    );
}

export default App;
